import { repositoryMutation, requiredString } from "./transaction.js";
export class SqliteConversationRepository {
    #database;
    #now;
    constructor(database, options = {}) {
        this.#database = database;
        this.#now = options.now ?? (() => new Date().toISOString());
    }
    appendPendingMessage(input) {
        return repositoryMutation(this.#database, (transaction) => {
            const current = transaction.selectObject("SELECT state, body, conversation_id, sender_id FROM conversation_messages WHERE id = ?", [input.message.id]);
            if (current !== undefined) {
                if (requiredString(current.body, "message body") !== input.message.body ||
                    requiredString(current.conversation_id, "message conversation_id") !== input.message.conversationId ||
                    requiredString(current.sender_id, "message sender_id") !== input.message.senderId)
                    throw new Error("Conversation message id belongs to a different message");
                return undefined;
            }
            insertMessage(transaction, input.message);
            return messageEvent("created", input.message.id, this.#now());
        });
    }
    applyDelta(delta) {
        return repositoryMutation(this.#database, (transaction) => {
            switch (delta.kind) {
                case "conversation.upsert":
                    return upsertConversation(transaction, delta.conversation, this.#now());
                case "message.append":
                    return upsertMessageDelta(transaction, delta.message, this.#now());
                case "message.ack":
                    return acknowledgeMessageDelta(transaction, delta, this.#now());
                case "message.fail":
                    return failMessageDelta(transaction, delta, this.#now());
                case "notification.upsert":
                    return upsertNotification(transaction, delta.notification, this.#now());
                case "notification.read":
                    return markNotificationReadDelta(transaction, delta, this.#now());
            }
        });
    }
    async acknowledgeMessage(input) {
        return await this.applyDelta({
            acknowledgedAt: input.acknowledgedAt,
            kind: "message.ack",
            messageId: input.messageId,
            ...(input.serverMessageId === undefined ? {} : { serverMessageId: input.serverMessageId }),
        }).commit();
    }
    async failMessage(input) {
        return await this.applyDelta({
            failedAt: input.failedAt,
            kind: "message.fail",
            messageId: input.messageId,
            reason: input.reason,
        }).commit();
    }
    async listConversations(params = {}) {
        const limit = boundedLimit(params.limit);
        const rows = params.before === undefined
            ? this.#database.selectObjects("SELECT id, title, participant_ids_json, unread_count, last_message_preview, last_message_at, updated_at FROM conversation_summaries ORDER BY updated_at DESC, id DESC LIMIT ?", [limit])
            : this.#database.selectObjects("SELECT id, title, participant_ids_json, unread_count, last_message_preview, last_message_at, updated_at FROM conversation_summaries WHERE updated_at < ? ORDER BY updated_at DESC, id DESC LIMIT ?", [params.before, limit]);
        return rows.map(conversationRecord);
    }
    async listMessages(conversationId, params = {}) {
        const limit = boundedLimit(params.limit);
        const rows = params.before === undefined
            ? this.#database.selectObjects("SELECT id, conversation_id, sender_id, body, state, client_message_id, server_message_id, failure_reason, created_at FROM conversation_messages WHERE conversation_id = ? ORDER BY created_at ASC, id ASC LIMIT ?", [conversationId, limit])
            : this.#database.selectObjects("SELECT id, conversation_id, sender_id, body, state, client_message_id, server_message_id, failure_reason, created_at FROM conversation_messages WHERE conversation_id = ? AND created_at < ? ORDER BY created_at ASC, id ASC LIMIT ?", [conversationId, params.before, limit]);
        return rows.map(messageRecord);
    }
    async listNotifications(params = {}) {
        const limit = boundedLimit(params.limit);
        const unread = params.unreadOnly === true ? " AND read_at IS NULL" : "";
        return this.#database.selectObjects(`SELECT id, notification_type, title, body, reference_id, read_at, created_at
       FROM client_notifications WHERE 1 = 1${unread} ORDER BY created_at DESC, id DESC LIMIT ?`, [limit]).map(notificationRecord);
    }
    markRead(input) {
        return repositoryMutation(this.#database, (transaction) => {
            const current = transaction.selectObject("SELECT read_at FROM client_notifications WHERE id = ?", [input.notificationId]);
            if (current === undefined)
                throw new Error("Notification was not found");
            if (current.read_at === input.readAt)
                return undefined;
            transaction.exec("UPDATE client_notifications SET read_at = ? WHERE id = ?", { bind: [input.readAt, input.notificationId] });
            return notificationEvent("updated", input.notificationId, this.#now());
        });
    }
}
function insertMessage(database, message) {
    database.exec(`INSERT INTO conversation_messages(
      id, conversation_id, sender_id, body, state, client_message_id,
      server_message_id, failure_reason, created_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`, { bind: [
            message.id,
            message.conversationId,
            message.senderId,
            message.body,
            message.state,
            message.clientMessageId ?? null,
            message.serverMessageId ?? null,
            message.failureReason ?? null,
            message.createdAt,
        ] });
}
function upsertConversation(database, conversation, occurredAt) {
    database.exec(`INSERT INTO conversation_summaries(
      id, title, participant_ids_json, unread_count, last_message_preview,
      last_message_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?)
    ON CONFLICT(id) DO UPDATE SET
      title = excluded.title,
      participant_ids_json = excluded.participant_ids_json,
      unread_count = excluded.unread_count,
      last_message_preview = excluded.last_message_preview,
      last_message_at = excluded.last_message_at,
      updated_at = excluded.updated_at`, { bind: [
            conversation.id,
            conversation.title,
            JSON.stringify(conversation.participantIds),
            conversation.unreadCount,
            conversation.lastMessagePreview ?? null,
            conversation.lastMessageAt ?? null,
            conversation.updatedAt,
        ] });
    return conversationEvent("updated", conversation.id, occurredAt);
}
function upsertMessageDelta(database, message, occurredAt) {
    const current = database.selectObject("SELECT state FROM conversation_messages WHERE id = ?", [message.id]);
    if (current === undefined) {
        insertMessage(database, message);
        return messageEvent("created", message.id, occurredAt);
    }
    const currentState = requiredString(current.state, "message state");
    if (messageRank(message.state) <= messageRank(currentState))
        return undefined;
    database.exec(`UPDATE conversation_messages SET body = ?, state = ?, client_message_id = ?,
      server_message_id = ?, failure_reason = ?, created_at = ? WHERE id = ?`, { bind: [
            message.body,
            message.state,
            message.clientMessageId ?? null,
            message.serverMessageId ?? null,
            message.failureReason ?? null,
            message.createdAt,
            message.id,
        ] });
    return messageEvent("updated", message.id, occurredAt);
}
function acknowledgeMessageDelta(database, delta, occurredAt) {
    const current = database.selectObject("SELECT state FROM conversation_messages WHERE id = ?", [delta.messageId]);
    if (current === undefined)
        throw new Error("Conversation message was not found");
    if (requiredString(current.state, "message state") === "confirmed")
        return undefined;
    database.exec("UPDATE conversation_messages SET state = 'confirmed', server_message_id = ? WHERE id = ?", { bind: [delta.serverMessageId ?? null, delta.messageId] });
    return messageEvent("updated", delta.messageId, occurredAt);
}
function failMessageDelta(database, delta, occurredAt) {
    const current = database.selectObject("SELECT state, failure_reason FROM conversation_messages WHERE id = ?", [delta.messageId]);
    if (current === undefined)
        throw new Error("Conversation message was not found");
    if (requiredString(current.state, "message state") === "failed" && current.failure_reason === delta.reason)
        return undefined;
    database.exec("UPDATE conversation_messages SET state = 'failed', failure_reason = ? WHERE id = ?", { bind: [delta.reason, delta.messageId] });
    return messageEvent("updated", delta.messageId, occurredAt);
}
function upsertNotification(database, notification, occurredAt) {
    database.exec(`INSERT INTO client_notifications(
      id, notification_type, title, body, reference_id, read_at, created_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?)
    ON CONFLICT(id) DO UPDATE SET
      notification_type = excluded.notification_type,
      title = excluded.title,
      body = excluded.body,
      reference_id = excluded.reference_id,
      read_at = excluded.read_at,
      created_at = excluded.created_at`, { bind: [
            notification.id,
            notification.type,
            notification.title,
            notification.body,
            notification.referenceId ?? null,
            notification.readAt ?? null,
            notification.createdAt,
        ] });
    return notificationEvent("updated", notification.id, occurredAt);
}
function markNotificationReadDelta(database, delta, occurredAt) {
    const current = database.selectObject("SELECT read_at FROM client_notifications WHERE id = ?", [delta.notificationId]);
    if (current === undefined)
        throw new Error("Notification was not found");
    if (current.read_at === delta.readAt)
        return undefined;
    database.exec("UPDATE client_notifications SET read_at = ? WHERE id = ?", { bind: [delta.readAt, delta.notificationId] });
    return notificationEvent("updated", delta.notificationId, occurredAt);
}
function conversationRecord(row) {
    const participantIds = JSON.parse(requiredString(row.participant_ids_json, "conversation participant ids"));
    if (!Array.isArray(participantIds) || !participantIds.every((value) => typeof value === "string"))
        throw new TypeError("Invalid conversation participant ids");
    return {
        id: requiredString(row.id, "conversation id"),
        ...(row.last_message_at === null ? {} : { lastMessageAt: requiredString(row.last_message_at, "conversation last_message_at") }),
        ...(row.last_message_preview === null ? {} : { lastMessagePreview: requiredString(row.last_message_preview, "conversation last_message_preview") }),
        participantIds,
        title: requiredString(row.title, "conversation title"),
        unreadCount: numberValue(row.unread_count, "conversation unread_count"),
        updatedAt: requiredString(row.updated_at, "conversation updated_at"),
    };
}
function messageRecord(row) {
    return {
        body: requiredString(row.body, "message body"),
        ...(row.client_message_id === null ? {} : { clientMessageId: requiredString(row.client_message_id, "message client_message_id") }),
        conversationId: requiredString(row.conversation_id, "message conversation_id"),
        createdAt: requiredString(row.created_at, "message created_at"),
        ...(row.failure_reason === null ? {} : { failureReason: requiredString(row.failure_reason, "message failure_reason") }),
        id: requiredString(row.id, "message id"),
        ...(row.server_message_id === null ? {} : { serverMessageId: requiredString(row.server_message_id, "message server_message_id") }),
        senderId: requiredString(row.sender_id, "message sender_id"),
        state: requiredString(row.state, "message state"),
    };
}
function notificationRecord(row) {
    return {
        body: requiredString(row.body, "notification body"),
        createdAt: requiredString(row.created_at, "notification created_at"),
        id: requiredString(row.id, "notification id"),
        ...(row.read_at === null ? {} : { readAt: requiredString(row.read_at, "notification read_at") }),
        ...(row.reference_id === null ? {} : { referenceId: requiredString(row.reference_id, "notification reference_id") }),
        title: requiredString(row.title, "notification title"),
        type: requiredString(row.notification_type, "notification type"),
    };
}
function conversationEvent(action, entityId, occurredAt) {
    return {
        entityId,
        event: { occurredAt, payload: { action, entityId, entityType: "conversation" }, type: "domain.entity-changed" },
    };
}
function messageEvent(action, entityId, occurredAt) {
    return {
        entityId,
        event: { occurredAt, payload: { action, entityId, entityType: "message" }, type: "domain.entity-changed" },
    };
}
function notificationEvent(action, entityId, occurredAt) {
    return {
        entityId,
        event: { occurredAt, payload: { action, entityId, entityType: "notification" }, type: "domain.entity-changed" },
    };
}
function messageRank(state) {
    return state === "pending" ? 0 : state === "confirmed" ? 1 : 2;
}
function boundedLimit(value) {
    if (value === undefined)
        return 50;
    if (!Number.isInteger(value) || value < 1 || value > 100)
        throw new RangeError("Conversation page limit must be between 1 and 100");
    return value;
}
function numberValue(value, label) {
    if (typeof value !== "number")
        throw new TypeError(`Expected ${label} to be a number`);
    return value;
}
//# sourceMappingURL=conversation-repository.js.map